import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChartLine } from "lucide-react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import { RevenueChartLegend } from "../revenue/RevenueChartLegend";
import {
  AnalyticsAreaFillDefs,
  analyticsAreaFillUrl,
  useAnalyticsChartInstanceId,
} from "../shared/chartAreaFill";
import { formatAnalyticsDayLabel } from "../shared/analyticsFormatting";
import { SERVICES_CHART_CARD_ICON_COLOR } from "../services/constants";
import {
  TEAM_STAFF_CHART_COLORS,
  TEAM_STAFF_DETAIL_CHART_HEIGHT,
} from "./constants";
import type { TeamStaffDailyPoint } from "./staffDetailTypes";

function CancelledDelaysTooltip({
  active,
  payload,
  label,
  series,
}: {
  active?: boolean;
  payload?: Array<{ dataKey?: string; value?: number }>;
  label?: string;
  series: Array<{ key: string; label: string; color: string }>;
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const seriesByKey = Object.fromEntries(
    series.map((item) => [item.key, item]),
  );

  return (
    <div className="grid min-w-[10rem] gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
      <p className="font-medium text-zinc-900">
        {formatAnalyticsDayLabel(label)}
      </p>
      {payload.map((item) => {
        const config = seriesByKey[String(item.dataKey)];
        if (!config) {
          return null;
        }

        return (
          <div
            key={config.key}
            className="flex items-center justify-between gap-4"
          >
            <div className="flex items-center gap-2">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: config.color }}
              />
              <span className="text-muted-foreground">{config.label}</span>
            </div>
            <span className="font-medium text-zinc-900">
              {Number(item.value ?? 0).toLocaleString("en-US")}
            </span>
          </div>
        );
      })}
    </div>
  );
}

interface TeamStaffCancelledDelaysChartProps {
  data: TeamStaffDailyPoint[];
}

export const TeamStaffCancelledDelaysChart: React.FC<
  TeamStaffCancelledDelaysChartProps
> = ({ data }) => {
  const { t } = useTranslation();

  const series = useMemo(
    () =>
      [
        {
          key: "delays" as const,
          label: t("analytics.team.table.delays", "Delays"),
          color: TEAM_STAFF_CHART_COLORS.delays,
        },
        {
          key: "tasksCancelled" as const,
          label: t("analytics.team.table.tasksCancelled", "Tasks cancelled"),
          color: TEAM_STAFF_CHART_COLORS.tasksCancelled,
        },
      ] as const,
    [t],
  );

  const chartConfig = useMemo(
    () =>
      ({
        delays: {
          label: t("analytics.team.table.delays", "Delays"),
          color: TEAM_STAFF_CHART_COLORS.delays,
        },
        tasksCancelled: {
          label: t("analytics.team.table.tasksCancelled", "Tasks cancelled"),
          color: TEAM_STAFF_CHART_COLORS.tasksCancelled,
        },
      }) satisfies ChartConfig,
    [t],
  );

  const maxValue = Math.max(
    ...data.map((point) => Math.max(point.delays, point.tasksCancelled)),
    2,
  );
  const yAxisMax = Math.ceil(maxValue / 2) * 2 || 4;
  const chartHeight = TEAM_STAFF_DETAIL_CHART_HEIGHT;
  const chartInstanceId = useAnalyticsChartInstanceId();

  return (
    <RevenueChartCard
      title={t(
        "analytics.team.charts.cancelledAndDelays",
        "Tasks cancelled and Delays",
      )}
      icon={<ChartLine />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      width="full"
      style={{
        height: chartHeight,
        minHeight: chartHeight,
      }}
      className="flex flex-col"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <ChartContainer
          config={chartConfig}
          className="min-h-0 w-full flex-1 !aspect-auto"
        >
          <ComposedChart
            data={data}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <defs>
              <AnalyticsAreaFillDefs
                seriesKeys={["delays"]}
                chartInstanceId={chartInstanceId}
              />
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="day"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval={4}
              className="text-xs text-muted-foreground"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={32}
              domain={[0, yAxisMax]}
              allowDecimals={false}
              className="text-xs text-muted-foreground"
            />
            <ChartTooltip
              cursor={false}
              content={<CancelledDelaysTooltip series={[...series]} />}
            />
            <Area
              type="monotone"
              dataKey="delays"
              stroke="var(--color-delays)"
              fill={analyticsAreaFillUrl("delays", chartInstanceId)}
              strokeWidth={1.5}
            />
            <Line
              type="monotone"
              dataKey="tasksCancelled"
              stroke={TEAM_STAFF_CHART_COLORS.tasksCancelled}
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ChartContainer>
        <p className="shrink-0 text-xs font-medium leading-normal text-[#737373]">
          {t("analytics.common.date", "Date")}
        </p>
        <RevenueChartLegend items={[...series]} />
      </div>
    </RevenueChartCard>
  );
};
